/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";
import React from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import StorageLicenseBar from "components/StorageLicenseBar";

// Material Dashboard 2 React context
import { useMaterialUIController, setLayout } from "context";
import { useAIAssistantOptional } from "components/AIAssistant";
import { AI_PANEL_WIDTH_CSS } from "components/AIAssistant/constants";

function DashboardLayout({ children }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const { pathname } = useLocation();
  const aiAssistant = useAIAssistantOptional();
  const aiPanelOpen = Boolean(aiAssistant?.isOpen);

  useEffect(() => {
    setLayout(dispatch, "dashboard");
  }, [pathname]);

  const isPresentationMaker = pathname.includes("presentationMakerPage");
  const isCompactNavbar = !pathname.startsWith("/users") && !pathname.startsWith("/students");
  const isFullWidth = isPresentationMaker;

  return (
    <MDBox
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        p: isFullWidth ? 0 : 3,
        ...(isFullWidth
          ? {}
          : isCompactNavbar
            ? { pt: 3, px: 3, pb: 3 }
            : {}),
        position: "relative",
        boxSizing: "border-box",
        bgcolor: isFullWidth || darkMode ? undefined : "#F1F5FB",
        // Pin Storage/License outside the scrollport; only page content scrolls.
        ...(!isFullWidth
          ? {
              display: "flex",
              flexDirection: "column",
              height: "100vh",
              maxHeight: "100vh",
              overflow: "hidden",
              "@supports (height: 100dvh)": {
                height: "100dvh",
                maxHeight: "100dvh",
              },
            }
          : { minHeight: "100vh" }),
        transition: transitions.create(["padding", "margin-left", "margin-right"], {
          easing: transitions.easing.easeInOut,
          duration: transitions.duration.standard,
        }),
        // Smoothly shrink main content when the persistent AI panel opens (desktop).
        [breakpoints.up("md")]: {
          marginRight: !isFullWidth && aiPanelOpen ? AI_PANEL_WIDTH_CSS : 0,
        },
        [breakpoints.up("xl")]: {
          marginLeft: isFullWidth ? 0 : miniSidenav ? pxToRem(120) : pxToRem(274),
        },
      })}
    >
      {!isFullWidth ? <StorageLicenseBar sticky={false} /> : null}
      {isFullWidth ? (
        children
      ) : (
        <MDBox
          data-dashboard-scroll="true"
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            // Keep the last cards fully visible above the scroll edge.
            pb: 2,
          }}
        >
          {children}
        </MDBox>
      )}
    </MDBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default DashboardLayout;
